import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, Checkbox, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { stageLabel } from "../utils/stages";
import { CatalogRow } from "./CicdPlantTargetCatalogRow";

// Werkskatalog als Tabelle (Welle 4, seit Welle 5 Tastatur- und Barrierefreiheitsweg des Werks-Instanz-Mappings):
// je Werk die Zielinstanzen der Mehrzielstufen. Nur Abweichungen werden gespeichert, ein Werk ohne Eintrag nutzt die
// Standardziele. Je Zelle genau eine Checkbox. Laden und Speichern liegen beim Aufrufer (PlantInstanceMapping), damit
// Grafik und Tabelle denselben Entwurf bearbeiten.
export const STAGE_ORDER = ["qas", "preprod", "prod"];
export const DEFAULT_ID = "default";
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** Ziel-ID einer Zeile im Katalog: das Standardziel heißt immer "default". */
export const idOf = row => row.isDefault ? DEFAULT_ID : row.targetId;
/** Anzeigename einer Zielzeile, mit Rückfall auf Environment und targetId. */
export const colName = row => row.instanceName || row.environment || row.targetId;

/**
 * Mehrzielstufen der GET-Antwort als `[{ key, rows }]`, geordnet nach STAGE_ORDER.
 * Bevorzugt die Form aus Vertrag V5.3 (`stages[].instances`, auch nicht eingerichtete Instanzen),
 * sonst die Form aus Welle 4 (`stageTargets`, nur Stufen mit zwei oder mehr Zielen).
 */
export function catalogColumns(data) {
  if (Array.isArray(data?.stages)) {
    return data.stages
      .filter(s => STAGE_ORDER.includes(s?.stage) && Array.isArray(s.instances) && s.instances.length > 1)
      .sort((a, b) => STAGE_ORDER.indexOf(a.stage) - STAGE_ORDER.indexOf(b.stage))
      .map(s => ({ key: s.stage, rows: s.instances.map(i => ({
        targetId: i.targetId, instanceId: i.instanceId, instanceName: i.name || i.instanceName || null,
        region: i.region || null, environment: i.environment || null, isDefault: !!i.isDefault, configured: i.configured !== false,
      })) }));
  }
  return STAGE_ORDER.filter(key => (data?.stageTargets?.[key]?.length || 0) > 1)
    .map(key => ({ key, rows: data.stageTargets[key].map(row => ({ ...row, configured: row.configured !== false })) }));
}

/**
 * Basisstufe: die erste Vorwärtsstufe mit Zielen. Ihr Standardziel bleibt immer gesetzt (Server: base-required).
 * Ohne `stageTargets` (reine V5.3-Form) gilt QAS, falls QAS eine Mehrzielstufe ist; sonst wird nichts gesperrt.
 */
export function baseStageOf(data, columns) {
  if (data?.stageTargets) return STAGE_ORDER.find(key => data.stageTargets[key]?.length) || null;
  return columns[0]?.key === STAGE_ORDER[0] ? STAGE_ORDER[0] : null;
}

/**
 * Auswahl je Stufe, geordnet nach den Zeilen. Genau `["default"]`, leere Stufen und Werke ohne Eintrag fallen weg.
 * Nur Kennungen aus `plantKeys` (falls angegeben) und nur übergebene Stufen (Mehrzielstufen) bleiben erhalten.
 * Die Instanz-ID eines Standardziels gilt defensiv als "default".
 */
export function normalizeCatalog(entries, stages, plantKeys = null) {
  const out = {};
  for (const [kennung, perStage] of Object.entries(entries || {})) {
    if (plantKeys && !plantKeys.includes(kennung)) continue;
    const next = {};
    for (const { key, rows } of stages) {
      const raw = perStage?.[key];
      if (!Array.isArray(raw)) continue;
      const chosen = raw.map(id => rows.some(row => row.isDefault && (row.targetId === id || row.instanceId === id)) ? DEFAULT_ID : id);
      const ids = rows.map(idOf).filter(id => chosen.includes(id));
      if (ids.length && !same(ids, [DEFAULT_ID])) next[key] = ids;
    }
    if (Object.keys(next).length) out[kennung] = next;
  }
  return out;
}

export const plantLabel = plant => [plant.name, plant.kennung].filter(Boolean).join(" ");

// MUI-Tabelle mit Theme-Variablen (Muster TargetNamesDialog.jsx). Die Kopfzeilen und die Werksspalte bleiben stehen.
// Alle sx-Objekte sind Konstanten; die Zeilen sind je Werk memoisiert (CicdPlantTargetCatalogRow).
const HEAD_ROW = 40;
const brandInk = { color: "var(--iio-brand-ink)", '[data-theme="dark"] &': { color: "var(--iio-info)" } };
const focusRing = { outline: "2px solid var(--iio-brand-fg)", outlineOffset: -2 };
const tableSx = {
  width: "max-content", minWidth: "100%", borderCollapse: "separate", fontVariantNumeric: "tabular-nums",
  // Kopfzellen bekommen sonst Hintergrund, Schriftgröße und Großschreibung aus muiTheme.js. Der Hintergrund bleibt deckend
  // (bg-1, nicht transparent), weil Kopfzeilen und Werksspalte beim Scrollen über den Zeilen stehen.
  "& .MuiTableCell-root": { fontFamily: "inherit", fontSize: 14, color: "var(--iio-text-1)", background: "var(--iio-bg-1)", backgroundColor: "var(--iio-bg-1)",
    borderBottom: "1px solid var(--iio-border-subtle)", px: 1.5, py: 1, verticalAlign: "middle", textTransform: "none", letterSpacing: "normal" },
  "& .MuiTableCell-head": { fontWeight: 600, color: "var(--iio-text-2)", whiteSpace: "nowrap" },
  "& thead tr:first-of-type .MuiTableCell-head": { top: 0, height: HEAD_ROW, boxSizing: "border-box" },
  "& thead tr:nth-of-type(2) .MuiTableCell-head": { top: HEAD_ROW, fontWeight: 500, fontSize: 13, verticalAlign: "top" },
  "& .ptc-plant": { position: "sticky", left: 0, zIndex: 1, minWidth: 200, maxWidth: 280, borderRight: "1px solid var(--iio-border)" },
  "& thead .ptc-plant": { zIndex: 3 },
  "& .ptc-groupstart": { borderLeft: "1px solid var(--iio-border)" },
  "& .MuiCheckbox-root": { p: 1, color: "var(--iio-text-3)" },
  "& .MuiCheckbox-root.Mui-checked": brandInk,
  "& .MuiCheckbox-root.Mui-disabled": { opacity: .55 },
  // Automatisches Standardziel: angehakt, aber deutlich gedämpft und gestrichelt umrandet wie die Standardkante der Grafik.
  "& .ptc-auto .MuiCheckbox-root": { opacity: .4 },
  "& .ptc-auto .MuiSvgIcon-root": { outline: "1px dashed var(--iio-text-3)", outlineOffset: 1, borderRadius: "2px" },
  "& .MuiCheckbox-root.Mui-focusVisible": focusRing,
};
const noteSx = { display: "block", fontSize: 12, color: "var(--iio-text-3)", fontFamily: "inherit", fontWeight: 500 };
const pendingNoteSx = { ...noteSx, color: "var(--iio-warning)", whiteSpace: "normal", maxWidth: 180 };
const hintSx = { mt: 1, fontSize: 13, lineHeight: 1.45, color: "var(--iio-text-2)", fontFamily: "inherit" };
const titleSx = { m: 0, mb: 1.5, fontSize: 14, fontWeight: 600, fontFamily: "inherit", color: "var(--iio-text-1)" };
const toolsSx = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mb: 1.5 };
export const searchSx = { width: 260, maxWidth: "100%",
  "& .MuiOutlinedInput-root": { borderRadius: "6px", fontFamily: "inherit", fontSize: 13, background: "var(--iio-bg-0)", backgroundColor: "var(--iio-bg-0)", color: "var(--iio-text-1)" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-border)" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-text-3)" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-brand-fg)", borderWidth: 1, boxShadow: "none" } };
const onlySx = { m: 0, minHeight: 44, "& .MuiFormControlLabel-label": { fontFamily: "inherit", fontSize: 14, color: "var(--iio-text-1)" } };
const onlyBoxSx = { color: "var(--iio-text-3)", "&.Mui-checked": brandInk, "&.Mui-focusVisible": focusRing };
const containerSx = { maxHeight: 460, border: "1px solid var(--iio-border)", borderRadius: "4px", background: "var(--iio-bg-1)" };
const sectionSx = { minWidth: 0, fontFamily: "inherit" };

/**
 * Kontrollierte Tabelle. `entries` ist der normalisierte Entwurf, `original` der geladene Stand (für den Filter
 * „Nur angepasste Werke“). `onChange(nextEntries)` liefert den neuen, noch nicht normalisierten Entwurf.
 *
 * Dieselbe Regel wie die Grafik (Korrekturrunde 1): Die gesetzten Haken einer Stufe sind genau die Ziele. Ohne eigenen
 * Haken gilt automatisch das Standardziel (angehakt, gedämpft, nicht bedienbar). Ein anderer Haken ersetzt die automatische
 * Zuordnung, ein ausdrücklich gesetzter Haken beim Standardziel bleibt bestehen, ohne Haken gilt wieder das Standardziel.
 * In der Basisstufe bleibt das Standardziel immer gesetzt (V.16).
 */
export default function CicdPlantTargetCatalog({ columns, baseStage, plants, entries, original, onChange, t, disabled = false, readOnly = false }) {
  const [query, setQuery] = useState("");
  const [onlyCustom, setOnlyCustom] = useState(false);
  const plantKeys = useMemo(() => plants.map(p => p.kennung), [plants]);
  const locked = disabled || readOnly;
  // Stabile Rückrufe für die memoisierten Zeilen: sie lesen den jeweils letzten Stand über eine Ref.
  const latest = useRef(null);
  latest.current = { entries, columns, baseStage, plantKeys, onChange };
  const toggle = useCallback((kennung, stage, id) => {
    const { entries: cur, columns: cols, baseStage: base, plantKeys: keys, onChange: emit } = latest.current;
    const own = cur[kennung]?.[stage] || null;
    let next = !own ? [id] : own.includes(id) ? own.filter(x => x !== id) : [...own, id];
    if (stage === base && next.length && !next.includes(DEFAULT_ID)) next = [DEFAULT_ID, ...next];
    const perStage = { ...(normalizeCatalog(cur, cols, keys)[kennung] || {}) };
    if (next.length) perStage[stage] = next; else delete perStage[stage];
    const { [kennung]: _drop, ...rest } = cur;
    emit(Object.keys(perStage).length ? { ...rest, [kennung]: perStage } : rest);
  }, []);
  const reset = useCallback(kennung => {
    const { entries: cur, onChange: emit } = latest.current;
    const { [kennung]: _drop, ...rest } = cur;
    emit(rest);
  }, []);

  if (columns.length === 0) return <Box className="cs-ptc"><Typography sx={hintSx}>{t("plantTargets.keineStufen")}</Typography></Box>;

  const needle = query.trim().toLowerCase();
  const visible = plants.filter(p => (!needle || `${p.name || ""} ${p.kennung}`.toLowerCase().includes(needle)) && (!onlyCustom || original?.[p.kennung]));

  return (
    <Box component="section" className="cs-ptc" aria-labelledby="cs-ptc-title" sx={sectionSx}>
      <Typography component="h3" id="cs-ptc-title" sx={titleSx}>{t("plantTargets.title")}</Typography>
      <Box sx={toolsSx}>
        <TextField size="small" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={t("plantTargets.search")}
          inputProps={{ "aria-label": t("plantTargets.search") }} sx={searchSx}/>
        <FormControlLabel label={t("plantTargets.onlyCustom")} sx={onlySx}
          control={<Checkbox size="small" disableRipple checked={onlyCustom} onChange={e => setOnlyCustom(e.target.checked)} sx={onlyBoxSx}/>}/>
      </Box>
      <TableContainer sx={containerSx}>
        <Table stickyHeader size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} className="ptc-plant" scope="col">{t("plantTargets.colPlant")}</TableCell>
              {columns.map(stage => <TableCell key={stage.key} colSpan={stage.rows.length} scope="colgroup" className="ptc-groupstart">{stageLabel(stage.key)}</TableCell>)}
              {!readOnly && <TableCell rowSpan={2} scope="col" aria-label={t("plantTargets.colAction")}/>}
            </TableRow>
            <TableRow>
              {columns.flatMap(stage => stage.rows.map((row, i) => <TableCell key={stage.key + idOf(row)} scope="col" className={i === 0 ? "ptc-groupstart" : undefined}>
                {colName(row)}{row.region ? ` (${row.region})` : ""}
                {row.isDefault && <Box component="span" sx={noteSx}>{t("plantMapping.isDefault")}</Box>}
                {row.configured === false && <Box component="span" sx={pendingNoteSx}>{t("plantMapping.notConfigured")}</Box>}
              </TableCell>))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.length === 0 && <TableRow><TableCell colSpan={(readOnly ? 1 : 2) + columns.reduce((n, s) => n + s.rows.length, 0)}>{t("plantTargets.keinTreffer")}</TableCell></TableRow>}
            {visible.map(plant => <CatalogRow key={plant.kennung} plant={plant} columns={columns} baseStage={baseStage} own={entries[plant.kennung]}
              locked={locked} disabled={disabled} readOnly={readOnly} onToggle={toggle} onReset={reset} t={t}/>)}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography id="cs-ptc-legend" sx={hintSx}>{t("plantTargets.legend")}</Typography>
    </Box>
  );
}
